/**
 * Event-driven localStorage change notification for same-window updates.
 *
 * The browser's native `StorageEvent` only fires for cross-tab changes.
 * This module patches `localStorage.setItem` and `localStorage.removeItem`
 * to also dispatch same-window notifications for watched keys.
 *
 * Usage:
 * - Call `watchStorageKey(key)` during app init to enable auto-notification.
 * - Subscribe in hooks via `subscribeToStorageKey(key, callback)`.
 * - Cross-tab changes are still handled by the native `StorageEvent`.
 */

type StorageChangeCallback = () => void;

const listeners = new Map<string, Set<StorageChangeCallback>>();
const watchedKeys = new Set<string>();
let patched = false;

/**
 * Notify all subscribers that a localStorage key has changed (same-window).
 */
function emitStorageChange(key: string): void {
  const keyListeners = listeners.get(key);
  if (keyListeners) {
    keyListeners.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error(
          `[localStorageEmitter] Error in listener for "${key}":`,
          err,
        );
      }
    });
  }
}

/**
 * Patch localStorage.setItem and removeItem to emit change events
 * for watched keys. Only patches once, idempotent.
 */
function patchLocalStorage(): void {
  if (patched || typeof window === "undefined" || !window.localStorage) return;
  patched = true;

  const originalSetItem = localStorage.setItem.bind(localStorage);
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);

  localStorage.setItem = (key: string, value: string) => {
    originalSetItem(key, value);
    if (watchedKeys.has(key)) {
      // Defer to next microtask to avoid re-entrancy during React render
      Promise.resolve().then(() => emitStorageChange(key));
    }
  };

  localStorage.removeItem = (key: string) => {
    originalRemoveItem(key);
    if (watchedKeys.has(key)) {
      Promise.resolve().then(() => emitStorageChange(key));
    }
  };
}

/**
 * Register a localStorage key for automatic same-window change notification.
 * When any code calls `localStorage.setItem(key, ...)` or `localStorage.removeItem(key)`,
 * all subscribers for that key will be notified.
 */
export function watchStorageKey(key: string): void {
  watchedKeys.add(key);
  patchLocalStorage();
}

/**
 * Subscribe to same-window AND cross-tab changes for a specific localStorage key.
 * Returns an unsubscribe function.
 *
 * The key must have been registered via `watchStorageKey(key)` for same-window
 * notifications to work. Cross-tab notifications (via `StorageEvent`) are also
 * handled automatically.
 */
export function subscribeToStorageKey(
  key: string,
  callback: StorageChangeCallback,
): () => void {
  if (!listeners.has(key)) {
    listeners.set(key, new Set());
  }
  listeners.get(key)!.add(callback);

  // Also handle cross-tab changes via native StorageEvent
  const handleStorageEvent = (e: StorageEvent) => {
    if (e.key === key) {
      callback();
    }
  };
  window.addEventListener("storage", handleStorageEvent);

  return () => {
    const keyListeners = listeners.get(key);
    if (keyListeners) {
      keyListeners.delete(callback);
      if (keyListeners.size === 0) {
        listeners.delete(key);
      }
    }
    window.removeEventListener("storage", handleStorageEvent);
  };
}
